// ============================================================================
// Bases y Propiedades — cliente de la API de bases, personal y resumen
// (módulo `basesPropiedades` de apps/torre-de-control-api, CC-CONSOLE #1592).
// La sesión y la compañía las pone la instancia compartida de axios; el
// servidor decide a qué compañía pertenece cada lectura.
// ============================================================================

import api, { apiFormData } from "../../../utils/api";

export const BASE_TYPES = ["cedis", "patio", "oficina", "bodega", "coworking", "estacionamiento"] as const;
export type BaseType = (typeof BASE_TYPES)[number];

export const BASE_STATUSES = ["active", "opening", "maintenance", "inactive"] as const;
export type BaseStatus = (typeof BASE_STATUSES)[number];

export const STAFF_ROLES = ["gerente", "community-manager", "recepcion", "ventas", "seguridad", "mantenimiento", "limpieza"] as const;
export const STAFF_STATUSES = ["active", "vacation", "inactive"] as const;
export type StaffStatus = (typeof STAFF_STATUSES)[number];

export interface BaseZone {
  id: string;
  name: string;
  type: string;
  capacity: number;
}

export interface BaseManager {
  name: string;
  phone?: string;
  email?: string;
}

export interface Base {
  id: string;
  name: string;
  code?: string;
  type: BaseType;
  status: BaseStatus;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  timezone?: string;
  latitude?: number | null;
  longitude?: number | null;
  capacity: number;
  capacityUnit?: string;
  occupancy: number;
  openTime?: string;
  closeTime?: string;
  open24h?: boolean;
  days?: string[];
  manager?: BaseManager;
  phone?: string;
  email?: string;
  photos: string[];
  zones: BaseZone[];
  truckYardId?: string | null;
  notes?: string;
  tags?: string[];
  staffCount?: number;
  updatedAt?: unknown;
}

export interface BaseStaff {
  id: string;
  base_id: string;
  baseName?: string;
  name: string;
  email?: string;
  phone?: string;
  role: string;
  shift?: string;
  hours?: string;
  status: StaffStatus;
}

export interface BasesSummary {
  bases: number;
  activeBases: number;
  byType: Record<string, number>;
  staff: number;
  capacity: number;
  occupancy: number;
  occupancyPct: number;
  amenities: number;
  bookingsToday: number;
  bookingsUpcoming: number;
  pendingBookings: number;
  rounds: {
    today: number;
    completedToday: number;
    compliancePctToday: number | null;
    compliancePctWeek: number | null;
  };
  openIncidents: number;
}

type Draft<T> = Omit<T, "id">;
const listOf = <T>(body: unknown): T[] => {
  const data = (body as { data?: unknown })?.data;
  return Array.isArray(data) ? (data as T[]) : [];
};
const docOf = <T>(body: unknown): T => (body as { data: T }).data;

/** Base del API → forma del front (rellena lo que falte en documentos viejos). */
export function normalizeBase(d: Partial<Base> & { id: string }): Base {
  return {
    ...d,
    id: d.id,
    name: d.name ?? "",
    type: (BASE_TYPES as readonly string[]).includes(String(d.type)) ? (d.type as BaseType) : "oficina",
    status: (BASE_STATUSES as readonly string[]).includes(String(d.status)) ? (d.status as BaseStatus) : "active",
    capacity: Number(d.capacity ?? 0),
    occupancy: Number(d.occupancy ?? 0),
    photos: Array.isArray(d.photos) ? d.photos : [],
    zones: Array.isArray(d.zones) ? d.zones : [],
  };
}

export async function listBases(): Promise<Base[]> {
  const res = await api.get("/bases/basesList");
  return listOf<Base>(res.data).map(normalizeBase);
}

function baseForm(draft: Partial<Draft<Base>>, files: File[]): FormData {
  const form = new FormData();
  for (const [k, v] of Object.entries(draft)) {
    if (v === undefined || k === "photos" || k === "staffCount" || k === "updatedAt") continue;
    form.append(k, typeof v === "object" && v !== null ? JSON.stringify(v) : String(v ?? ""));
  }
  // Sólo si se editaron las fotos: sin `existingPhotos` el servidor las deja como están.
  if (draft.photos !== undefined || files.length) form.append("existingPhotos", JSON.stringify(draft.photos ?? []));
  files.forEach((f, i) => form.append(`photo${i}`, f));
  return form;
}

export async function createBase(draft: Partial<Draft<Base>>, files: File[] = []): Promise<Base> {
  const res = await apiFormData.post("/bases/base", baseForm(draft, files));
  return normalizeBase(docOf(res.data));
}

export async function updateBase(id: string, draft: Partial<Draft<Base>>, files: File[] = []): Promise<Base> {
  const res = await apiFormData.put("/bases/base", baseForm(draft, files), { params: { id } });
  return normalizeBase(docOf(res.data));
}

export async function deleteBase(id: string): Promise<void> {
  await api.delete("/bases/base", { params: { id } });
}

export async function getSummary(): Promise<BasesSummary> {
  const res = await api.get("/bases/summary");
  return docOf<BasesSummary>(res.data);
}

export async function listStaff(baseId?: string): Promise<BaseStaff[]> {
  const res = await api.get("/bases/staffList", { params: baseId ? { baseId } : {} });
  return listOf<BaseStaff>(res.data);
}

export async function createStaff(draft: Draft<BaseStaff>): Promise<BaseStaff> {
  const res = await api.post("/bases/staff", draft);
  return docOf(res.data);
}

export async function updateStaff(id: string, patch: Partial<Draft<BaseStaff>>): Promise<BaseStaff> {
  const res = await api.put("/bases/staff", patch, { params: { id } });
  return docOf(res.data);
}

export async function deleteStaff(id: string): Promise<void> {
  await api.delete("/bases/staff", { params: { id } });
}

/** Mensaje legible de un error del API (o del red). */
export function apiErrorMessage(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { data?: { message?: string }; message?: string; meta?: { message?: string } } }; message?: string };
  return e?.response?.data?.data?.message || e?.response?.data?.message || e?.response?.data?.meta?.message || fallback;
}
